/**
 * TechnicianRow Component
 *
 * Expandable row displaying a single technician's performance.
 * Collapsed: Name, rate, FRH, gross, ELR.
 * Expanded: Daily breakdown table showing each day's metrics.
 */

import React from 'react';
import { ChevronRightIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { Technician, TechDailyEntry } from './types';

interface TechnicianRowProps {
  technician: Technician;
  dailyEntries: TechDailyEntry[];
  isExpanded: boolean;
  onToggle: () => void;
  rank: number;
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

function formatNumber(value: number | null, decimals: number = 1): string {
  if (value === null || value === undefined) return '-';
  return value.toFixed(decimals);
}

function formatDate(dateStr: string): string {
  const [, month, day] = dateStr.split('-');
  return `${parseInt(month)}/${parseInt(day)}`;
}

function getDayOfWeek(dateStr: string): string {
  const date = new Date(dateStr + 'T12:00:00');
  return date.toLocaleDateString('en-US', { weekday: 'short' });
}

export const TechnicianRow: React.FC<TechnicianRowProps> = ({
  technician,
  dailyEntries,
  isExpanded,
  onToggle,
  rank,
}) => {
  return (
    <div className="border-b border-gray-100 last:border-b-0">
      {/* Main row */}
      <div
        className="flex items-center justify-between p-3 hover:bg-gray-50 cursor-pointer transition-colors"
        onClick={onToggle}
      >
        <div className="flex items-center gap-3 min-w-0">
          <span className="text-gray-400 transition-transform duration-200">
            {isExpanded ? (
              <ChevronDownIcon className="h-4 w-4" />
            ) : (
              <ChevronRightIcon className="h-4 w-4" />
            )}
          </span>
          <span className="text-xs text-gray-400 font-medium w-5">{rank}</span>
          <div className="min-w-0">
            <span className="font-medium text-gray-900 truncate block">
              {technician.techName}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-4 flex-shrink-0 text-sm">
          <div className="text-right w-16">
            <span className="text-gray-900 font-medium">
              {formatNumber(technician.flatRateHoursTotal)}
            </span>
            <span className="text-gray-400 text-xs block">FRH</span>
          </div>
          <div className="text-right w-20">
            <span className="text-lg font-bold text-teal-600">
              {formatCurrency(technician.laborGrossTotal)}
            </span>
          </div>
          <div className="text-right w-16 hidden md:block">
            <span className="text-gray-900">
              {technician.elrTotal !== null
                ? `$${formatNumber(technician.elrTotal, 0)}`
                : '-'}
            </span>
            <span className="text-gray-400 text-xs block">ELR</span>
          </div>
        </div>
      </div>

      {/* Expanded details - daily breakdown */}
      {isExpanded && (
        <div className="ml-12 mr-3 mb-3 bg-gray-50 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-100 text-gray-600 text-xs uppercase tracking-wider">
                <th className="text-left py-2 px-3 font-medium">Date</th>
                <th className="text-right py-2 px-3 font-medium">FRH</th>
                <th className="text-right py-2 px-3 font-medium">Sales</th>
                <th className="text-right py-2 px-3 font-medium">Cost</th>
                <th className="text-right py-2 px-3 font-medium">Gross</th>
                <th className="text-right py-2 px-3 font-medium">Jobs</th>
              </tr>
            </thead>
            <tbody>
              {dailyEntries.length === 0 ? (
                <tr>
                  <td colSpan={6} className="py-4 px-3 text-center text-gray-500">
                    No daily data available
                  </td>
                </tr>
              ) : (
                <>
                  {dailyEntries.map((entry) => (
                    <tr
                      key={entry.date}
                      className="border-t border-gray-200 hover:bg-white"
                    >
                      <td className="py-2 px-3 text-gray-700">
                        {formatDate(entry.date)}{' '}
                        <span className="text-gray-400 text-xs">
                          {getDayOfWeek(entry.date)}
                        </span>
                      </td>
                      <td className="py-2 px-3 text-right text-gray-900">
                        {formatNumber(entry.flatRateHours)}
                      </td>
                      <td className="py-2 px-3 text-right text-gray-900">
                        {formatCurrency(entry.laborSales)}
                      </td>
                      <td className="py-2 px-3 text-right text-gray-900">
                        {formatCurrency(entry.laborCost)}
                      </td>
                      <td className="py-2 px-3 text-right text-gray-900 font-medium">
                        {formatCurrency(entry.laborGross)}
                      </td>
                      <td className="py-2 px-3 text-right text-gray-900">
                        {entry.jobCount}
                      </td>
                    </tr>
                  ))}
                  {/* Totals row */}
                  <tr className="border-t-2 border-gray-300 bg-gray-100 font-medium">
                    <td className="py-2 px-3 text-gray-700">Total</td>
                    <td className="py-2 px-3 text-right text-gray-900">
                      {formatNumber(technician.flatRateHoursTotal)}
                    </td>
                    <td className="py-2 px-3 text-right text-gray-900">
                      {formatCurrency(technician.laborSalesTotal)}
                    </td>
                    <td className="py-2 px-3 text-right text-gray-900">
                      {formatCurrency(technician.laborCostTotal)}
                    </td>
                    <td className="py-2 px-3 text-right text-teal-600 font-bold">
                      {formatCurrency(technician.laborGrossTotal)}
                    </td>
                    <td className="py-2 px-3 text-right text-gray-900">
                      {dailyEntries.reduce((sum, e) => sum + e.jobCount, 0)}
                    </td>
                  </tr>
                </>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
